import { RouteName } from "~/types/routes";
import type { LocationQuery, LocationQueryValue } from "vue-router";

/**
 * Legacy/external links point at /prices with a b2b flag and qs-style filters,
 * e.g. /prices/?b2b=true&filters[carrier][$or][0][$in][0]=bring
 *
 * The query is not deep-parsed (only stringifyQuery is overridden in router.options.ts),
 * so the filter keys arrive flat. We pull the carrier out and hand it to the b2b-prices
 * page as ?carrier=..., which it picks up on mount.
 *
 * Must run before 04.page_access: accounts with the consignments service (the b2b audience)
 * do not have access to the prices page and would be sent to unauthorized instead.
 */

const CARRIER_FILTER_KEY = /^filters\[carrier\]/;

const firstValue = (value: LocationQueryValue | LocationQueryValue[]): string | undefined => {
  const val = Array.isArray(value) ? value.find((v) => !!v) : value;
  return val ? String(val) : undefined;
};

const getCarrier = (query: LocationQuery): string | undefined => {
  for (const [key, value] of Object.entries(query)) {
    if (!CARRIER_FILTER_KEY.test(key)) continue;

    const carrier = firstValue(value);
    if (carrier) return carrier;
  }

  return firstValue(query.carrier);
};

export default defineNuxtRouteMiddleware((to) => {
  if (to.path.replace(/\/+$/, "") !== "/prices") return;
  if (!("b2b" in to.query) || to.query.b2b === "false") return;

  const carrier = getCarrier(to.query);

  return navigateTo({
    name: RouteName.b2b_prices,
    query: carrier ? { carrier } : {},
    hash: to.hash,
  });
});
